/**
 * SmartImprove landing page (/improve).
 *
 * Visual structure mirrors /evaluate (EvaluatePage.jsx) one-for-one — same
 * card width / spacing / typography / button styles. Only the copy and the
 * navigation targets differ.
 *
 * Feb 2026 — Resume Improvement UX correction:
 *   - Labels use "Improvement" (not "Engagement") in the public flow.
 *   - "New Improvement" ALWAYS opens Step 1 clean — server-side drafts are
 *     left intact, local refs are ignored for this session (the intake page
 *     respects the `?new=1` query and skips auto-resume).
 *   - "Resume Improvement" ALWAYS asks for email first, validates format,
 *     and then ALWAYS shows the selection list (even with 0 or 1 drafts).
 *   - Filtering: drafts with submitted/finalized statuses are pruned via the
 *     backend reconcile pass — only resumable records remain.
 */
import { useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowRight, ArrowLeft, Wrench, Info, Building2, Mail, Clock,
  Warehouse, ListChecks,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  listDraftRefs,
  resumeIntakeDraft,
  clearDraftRef,
} from "../lib/smartimprovePublicIntakeApi";

const isValidEmail = (e) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

// Statuses that are NOT resumable. The backend returns 409 for these on the
// resume endpoint, but we also defensively filter here in case future backend
// payloads expose `status` directly.
const NON_RESUMABLE_STATUSES = new Set([
  "submitted",
  "ready_for_review",
  "finalized",
  "published",
  "completed",
]);

function ResumeDialog({
  open,
  onOpenChange,
  phase,
  email,
  emailError,
  onEmailChange,
  onEmailSubmit,
  drafts,
  loading,
  onResume,
  onStartNew,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="resume-dialog">
        <DialogHeader>
          <DialogTitle>Resume SmartImprove Improvement</DialogTitle>
          <DialogDescription>
            {phase === "email"
              ? "Enter the email used when starting this public improvement on this browser so we can look up your saved drafts."
              : drafts.length > 0
              ? "Pick the improvement you'd like to continue. Submitted assessments are not shown."
              : ""}
          </DialogDescription>
        </DialogHeader>

        {phase === "email" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onEmailSubmit();
            }}
            noValidate
            className="space-y-3 py-2"
            data-testid="resume-email-form"
          >
            <label className="block text-xs font-medium text-[#374151]">
              Contact email <span className="text-[#B91C1C]">*</span>
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="name@company.com"
              className="rounded-sm text-xs"
              data-testid="resume-email-input"
              autoFocus
            />
            {emailError ? (
              <p
                className="text-[10px] text-[#B91C1C]"
                data-testid="resume-email-error"
              >
                {emailError}
              </p>
            ) : (
              <p className="text-[10px] text-[#94A3B8]">
                We only show public improvements saved on this browser that
                match this email and are still in progress.
              </p>
            )}
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-sm text-xs"
                data-testid="resume-email-back"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
                data-testid="resume-email-submit"
              >
                Find my improvements
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DialogFooter>
          </form>
        ) : loading ? (
          <div
            className="py-8 text-center text-xs text-[#6B7280]"
            data-testid="resume-loading"
          >
            Checking your saved improvements…
          </div>
        ) : drafts.length === 0 ? (
          <>
            <div className="py-8 text-center" data-testid="resume-empty-state">
              <div className="flex justify-center mb-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F0F7FA] text-[#025979]">
                  <Wrench className="w-5 h-5" />
                </span>
              </div>
              <p className="text-sm text-[#374151]">
                No saved public SmartImprove draft found on this browser.
              </p>
              <p className="text-xs text-[#6B7280] mt-2 max-w-md mx-auto leading-relaxed">
                If this improvement was created by a consultant, please continue
                from the consultant workspace.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-sm text-xs"
                data-testid="resume-empty-back"
              >
                Back
              </Button>
              <Button
                onClick={onStartNew}
                className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
                data-testid="resume-empty-start-new"
              >
                Start New Improvement
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <ul
              className="space-y-2 max-h-[420px] overflow-y-auto pr-1"
              data-testid="resume-list"
            >
              {drafts.map((d) => (
                <li
                  key={d.id}
                  className="border border-[#E5E7EB] rounded-sm p-3 hover:border-[#025979] transition"
                  data-testid={`resume-card-${d.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 className="w-3.5 h-3.5 text-[#025979] shrink-0" />
                        <p className="text-sm font-semibold text-[#111827] truncate">
                          {d.company_name || "Untitled improvement"}
                        </p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F0F7FA] text-[#025979] border border-[#BAE6FD]">
                          Step {d.step || 1}
                        </span>
                        {d.status ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569] border border-[#E5E7EB] capitalize">
                            {String(d.status).replace(/_/g, " ")}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#6B7280] flex-wrap">
                        {d.email ? (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {d.email}
                          </span>
                        ) : null}
                        <span className="flex items-center gap-1">
                          <Warehouse className="w-3 h-3" />
                          {d.sites_count ?? 0} warehouse
                          {(d.sites_count ?? 0) === 1 ? "" : "s"}
                        </span>
                        <span className="flex items-center gap-1">
                          <ListChecks className="w-3 h-3" />
                          {d.findings_count ?? 0} finding
                          {(d.findings_count ?? 0) === 1 ? "" : "s"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {d.updated_at
                            ? new Date(d.updated_at).toLocaleString()
                            : "—"}
                        </span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => onResume(d)}
                      className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-[11px] h-8 px-3 shrink-0"
                      data-testid={`resume-card-btn-${d.id}`}
                    >
                      Resume
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-sm text-xs"
                data-testid="resume-list-back"
              >
                Back
              </Button>
              <Button
                onClick={onStartNew}
                className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
                data-testid="resume-list-start-new"
              >
                Start New Improvement
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ImprovePage() {
  const navigate = useNavigate();
  const [resumeOpen, setResumeOpen] = useState(false);
  const [phase, setPhase] = useState("email"); // "email" | "list"
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);

  const openResume = () => {
    // ALWAYS start with the email phase — never auto-resume from local cache.
    setEmail("");
    setEmailError("");
    setDrafts([]);
    setPhase("email");
    setResumeOpen(true);
  };

  const onEmailSubmit = async () => {
    const trimmed = (email || "").trim();
    if (!trimmed) {
      setEmailError("Email is required.");
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError("");
    setPhase("list");
    setLoading(true);

    // Match local refs to this email (case-insensitive). Refs that lack an
    // email field are skipped — we cannot prove ownership.
    const target = trimmed.toLowerCase();
    const refs = listDraftRefs().filter(
      (r) => (r.email || "").trim().toLowerCase() === target
    );
    if (refs.length === 0) {
      setDrafts([]);
      setLoading(false);
      return;
    }
    // Reconcile each local ref against the backend in parallel — submitted
    // (409) and missing (404) drafts are pruned, the rest get fresh metadata.
    const settled = await Promise.allSettled(
      refs.map((r) => resumeIntakeDraft(r.id, r.resume_token))
    );
    const live = [];
    settled.forEach((res, idx) => {
      const ref = refs[idx];
      if (res.status === "fulfilled") {
        const d = res.value;
        const status = d.engagement?.status;
        if (status && NON_RESUMABLE_STATUSES.has(status)) {
          clearDraftRef(ref.id);
          return;
        }
        // Defensive email re-check against the server payload.
        const serverEmail = (d.engagement?.email || "").trim().toLowerCase();
        if (serverEmail && serverEmail !== target) return;
        live.push({
          ...ref,
          company_name: d.engagement?.company_name || ref.company_name,
          email: d.engagement?.email || ref.email,
          status: status || ref.status,
          sites_count: (d.sites || []).length,
          findings_count: (d.findings || []).length,
          step:
            (d.findings || []).length > 0
              ? 3
              : (d.sites || []).length > 0
              ? 2
              : 2,
          updated_at: d.engagement?.updated_at || ref.updated_at,
        });
      } else {
        clearDraftRef(ref.id);
      }
    });
    live.sort(
      (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
    );
    setDrafts(live);
    setLoading(false);
  };

  const onResumePick = (d) => {
    setResumeOpen(false);
    navigate(`/improve/start?resume=${d.id}`);
  };

  const onStartNew = () => {
    setResumeOpen(false);
    navigate("/improve/start?new=1");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header />
      <main className="flex-1 flex items-start justify-center pt-8 sm:pt-12 pb-12">
        <div className="w-full max-w-xl mx-auto px-4 sm:px-6">
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8 sm:p-10">
            <div className="flex justify-center mb-5">
              <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5">
                <Wrench className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                <span className="text-xs font-medium text-[#025979]">
                  AI-Assisted Improvement
                </span>
              </div>
            </div>
            <h1
              data-testid="improve-page-title"
              className="text-center text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827] leading-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              SmartImprove
            </h1>
            <p
              data-testid="improve-page-subtitle"
              className="mt-3 text-center text-sm sm:text-base text-[#6B7280] max-w-md mx-auto leading-relaxed"
            >
              Identify gaps and optimize warehouse performance for your existing
              warehouse issues.
            </p>
            <div
              data-testid="improve-step-indicator"
              className="mt-5 flex items-center justify-center gap-2"
            >
              <div className="w-5 h-5 rounded-full bg-[#025979] flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">1</span>
              </div>
              <span className="text-xs font-medium text-[#111827]">
                Start by capturing the customer&apos;s warehouse context
              </span>
            </div>
            <div className="mt-7 mb-6 border-t border-[#E5E7EB]"></div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                data-testid="improve-start-btn"
                onClick={() => navigate("/improve/start?new=1")}
                className="bg-[#025979] hover:bg-[#03738C] text-white px-8 py-2.5 text-sm font-medium rounded-sm transition-colors shadow-sm"
              >
                New Improvement
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                data-testid="improve-resume-btn"
                variant="outline"
                onClick={openResume}
                className="rounded-sm text-sm border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors"
              >
                Resume Improvement
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="mt-4 flex justify-center">
              <button
                data-testid="improve-back-btn"
                onClick={() => navigate("/warehousex/smartstrategy")}
                className="text-xs text-[#6B7280] hover:text-[#025979] flex items-center gap-1.5 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to SmartStrategy Home
              </button>
            </div>
            <p
              data-testid="improve-duration-hint"
              className="mt-4 text-center text-xs text-[#9CA3AF]"
            >
              Takes ~5–7 minutes to set up your improvement
            </p>
            <div
              data-testid="improve-info-hint"
              className="mt-5 flex items-start gap-2 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3.5 py-2.5"
            >
              <Info
                className="w-3.5 h-3.5 text-[#025979] mt-0.5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="text-xs text-[#6B7280] leading-relaxed">
                Capture issues and opportunities, then use our intelligence engine
                to generate insights, recommendations, and ROI.
              </span>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <ResumeDialog
        open={resumeOpen}
        onOpenChange={setResumeOpen}
        phase={phase}
        email={email}
        emailError={emailError}
        onEmailChange={(v) => {
          setEmail(v);
          if (emailError) setEmailError("");
        }}
        onEmailSubmit={onEmailSubmit}
        drafts={drafts}
        loading={loading}
        onResume={onResumePick}
        onStartNew={onStartNew}
      />
    </div>
  );
}
